import React, { useState, useEffect, useCallback } from "react";
import { Box, Switch, Tooltip, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningIcon from '@mui/icons-material/Warning';
import api from "../../Api/apiAxios";
import Loading from "../../Components/Loading";
import Mensagem from "../../Components/Mensagem";
import { ButtonBase, TextFieldBase } from "../Pedidos/styles";
import PainelCard from "./PainelCard";
import ModalInconsistencias from "../IntegracaoSisdm/ModalInconsistencias";
import { listarInconsistencias } from "../IntegracaoSisdm/integracaoApi";

const FaturamentoCard = () => {
    const nivelUsuario = window.localStorage.getItem('nivelUsuario');
    const idUsuario = window.localStorage.getItem('idUsuario');

    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState(null);
    const [enviarEmail, setEnviarEmail] = useState(false);
    const [mensagemErro, setMensagemErro] = useState('');
    const [periodo, setPeriodo] = useState("");
    const [inconsistencias, setInconsistencias] = useState([]);
    const [showInconsistencias, setShowInconsistencias] = useState(false);
    const [showAvisoConsolidar, setShowAvisoConsolidar] = useState(false);

    useEffect(() => {
        const periodoAtual = getPeriodoAtualFormatado();
        setPeriodo(periodoAtual);
    }, []);

    function periodoFormatoConsulta() {
        return periodo.slice(0, 7).replace('-', '');
    }

    const carregarInconsistencias = useCallback(async () => {
        if (!/^\d{6}$/.test(periodoFormatoConsulta())) return;

        try {
            const itens = await listarInconsistencias(periodoFormatoConsulta());
            setInconsistencias(itens);
        } catch (error) {
            console.log("Erro ao verificar inconsistências do faturamento: ", error);
            setInconsistencias([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodo]);

    useEffect(() => {
        carregarInconsistencias();
    }, [carregarInconsistencias]);

    const getPeriodoAtualFormatado = () => {
        const dataAtual = new Date();
        const dia = dataAtual.getDate();

        const mesReferencia = dia > 25 ? dataAtual.getMonth() + 1 : dataAtual.getMonth();
        const anoReferencia = dia > 25 && mesReferencia === 12 ? dataAtual.getFullYear() + 1 : dataAtual.getFullYear();

        const mesFormatado = String(mesReferencia + 1).padStart(2, "0");

        return `${anoReferencia}-${mesFormatado}-01`;
    }

    function handleChangePeriodo(e) {
        setPeriodo(`${e.target.value}-01`);
    }

    async function handleGerarFaturamento(ev) {
        ev.preventDefault();
        setLoading(true);

        try {
            const msgContrato = await handleGerarFaturamentoContrato();
            const msgPedidos = await handleGerarFaturamentoPedidos();
            const msgServicos = await handleGerarFaturamentoServicos();

            const mensagens = [msgContrato, msgPedidos, msgServicos].filter(Boolean);

            const periodoJaConsolidado = mensagens.every(msg =>
                msg.includes("já foi consolidado") ||
                msg.includes("Não é possível refazer o faturamento")
            );

            if (periodoJaConsolidado) {
                setMensagem(`O faturamento do período já está consolidado. Não é possível refazer o faturamento.`);
                return;
            }

            try {
                await api.post('/Faturamento/registrar-log-geracao', null, {
                    params: {
                        periodo: periodo,
                        usuarioId: idUsuario,
                        emailEnviado: enviarEmail
                    }
                });
            } catch (error) {
                console.log("Erro ao registrar log de faturamento: ", error);
            }

            setMensagem(`Faturamento gerado com sucesso!\n${mensagens.join("\n")}`);
        } catch (error) {
            console.log("Erro ao gerar faturamento: ", error);
            setMensagemErro(error.message || "Erro desconhecido.");
        } finally {
            setLoading(false);
            carregarInconsistencias();
        }
    }

    function handleConsolidarFaturamento() {
        if (inconsistencias.length > 0) {
            setShowAvisoConsolidar(true);
            return;
        }
        executarConsolidacao();
    }

    function confirmarConsolidacaoComInconsistencias() {
        setShowAvisoConsolidar(false);
        executarConsolidacao();
    }

    async function executarConsolidacao() {
        try {
            const response = await api.post('/Faturamento/consolidar', null, {
                params: {
                    periodo: periodo,
                    usuarioConsolidacaoId: idUsuario
                }
            });

            if (response.data?.sucesso) {
                setMensagem(response.data.mensagem || "Faturamento consolidado com sucesso!");
            }
        } catch (error) {
            console.log("Erro ao consolidar faturamento: ", error);
            setMensagemErro(error?.response?.data?.mensagem || error.message || "Erro desconhecido.");
        } finally {
            carregarInconsistencias();
        }
    }

    async function handleGerarFaturamentoContrato() {
        const email = enviarEmail ? 1 : 0;
        try {
            const response = await api.get(`/Faturamento/${email}/${periodo}`, {
                params: {
                    usuarioId: idUsuario
                }
            });
            return response.data;
        } catch (error) {
            console.log("Erro ao gerar faturamento contratos: ", error);
            return "Erro ao gerar faturamento de contratos.";
        }
    }

    async function handleGerarFaturamentoPedidos() {
        try {
            const response = await api.get(`/Faturamento/pedidos/${periodo}`, {
                params: {
                    usuarioId: idUsuario
                }
            });
            return response.data;
        } catch (error) {
            console.log("Erro ao gerar faturamento pedidos: ", error);
            return "Erro ao gerar faturamento de pedidos.";
        }
    }

    async function handleGerarFaturamentoServicos() {
        try {
            const response = await api.get(`/Faturamento/servicos/${periodo}`, {
                params: {
                    usuarioId: idUsuario
                }
            });
            return response.data;
        } catch (error) {
            console.log("Erro ao gerar faturamento serviços: ", error);
            return "Erro ao gerar faturamento de serviços.";
        }
    }

    return (
        <PainelCard
            icon={<ReceiptLongOutlinedIcon />}
            iconBg="rgba(25, 118, 210, 0.12)"
            iconColor="#1976d2"
            title="Geração de Faturamento"
            chipLabel="Mensal"
            chipBg="rgba(46, 125, 50, 0.12)"
            chipColor="#2e7d32"
            description="Gera o faturamento de contratos, pedidos e serviços liberados dentro do período selecionado."
        >
            {inconsistencias.length > 0 && (
                <Box
                    component="button"
                    type="button"
                    onClick={() => setShowInconsistencias(true)}
                    aria-label="Verificar inconsistências"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: { xs: '1.5rem', md: '2rem' },
                        transform: 'translateY(-40%)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        border: 'none',
                        backgroundColor: 'rgba(236, 160, 52, 0.3)',
                        color: '#b26a00',
                        borderRadius: '999px',
                        cursor: 'pointer',
                        p: 0,
                        height: '2.25rem',
                        width: '2.25rem',
                        boxShadow: '0 2px 6px rgba(237, 108, 2, 0.18)',
                        transition: 'width 0.25s ease, background-color 0.2s ease',
                        '&:hover': {
                            width: '13.5rem',
                            backgroundColor: 'rgba(237, 108, 2, 0.2)'
                        },
                        '&:hover .texto-verificar-inconsistencias': {
                            maxWidth: '11rem',
                            opacity: 1
                        },
                        '&:focus-visible': {
                            width: '13.5rem',
                            outline: '2px solid #ed6c02',
                            outlineOffset: '2px'
                        },
                        '&:focus-visible .texto-verificar-inconsistencias': {
                            maxWidth: '11rem',
                            opacity: 1
                        }
                    }}
                >
                    <Box sx={{ width: '2.25rem', height: '2.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <WarningIcon sx={{ fontSize: '1.15rem' }} />
                    </Box>
                    <Typography
                        variant="body2"
                        className="texto-verificar-inconsistencias"
                        sx={{
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            maxWidth: 0,
                            opacity: 0,
                            overflow: 'hidden',
                            transition: 'max-width 0.25s ease, opacity 0.15s ease'
                        }}
                    >
                        Verificar inconsistências
                    </Typography>
                </Box>
            )}

            {loading ? (
                <Loading />
            ) : (
                <>
                    {mensagemErro && <Mensagem mensagem={mensagemErro} setMensagem={setMensagemErro} variant="danger" />}

                    <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, fontWeight: 500, color: '#132350' }}>
                            Selecione o mês
                            <Tooltip title="Mês de referência utilizado para gerar o faturamento de contratos, pedidos e serviços.">
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                            </Tooltip>
                        </Typography>
                        <TextFieldBase
                            id="periodo-faturamento"
                            type="month"
                            fullWidth
                            value={periodo.slice(0, 7)}
                            onChange={handleChangePeriodo}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'rgba(19, 35, 80, 0.03)',
                            borderRadius: '12px',
                            p: 2,
                            mt: 3,
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#132350', textAlign: 'left' }}>
                                Enviar e-mail para as revendas
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Notifica as revendas quando o faturamento for finalizado.
                            </Typography>
                        </Box>
                        <Switch checked={enviarEmail} onChange={() => setEnviarEmail(!enviarEmail)} />
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3 }}>
                        <ButtonBase
                            onClick={handleGerarFaturamento}
                            variant="contained"
                            startIcon={<BoltIcon />}
                            disabled={nivelUsuario !== "1"}
                        >
                            Gerar Faturamento
                        </ButtonBase>

                        <ButtonBase
                            onClick={handleConsolidarFaturamento}
                            variant="outlined"
                            startIcon={<TaskAltIcon />}
                            disabled={nivelUsuario !== "1"}
                        >
                            Consolidar Faturamento
                        </ButtonBase>
                    </Box>

                    {mensagem && <Mensagem mensagem={mensagem} setMensagem={setMensagem} variant="warning" />}
                </>
            )}

            <ModalInconsistencias
                show={showInconsistencias}
                onHide={() => setShowInconsistencias(false)}
                periodo={periodoFormatoConsulta()}
                itens={inconsistencias}
            />

            <ModalInconsistencias
                show={showAvisoConsolidar}
                onHide={() => setShowAvisoConsolidar(false)}
                onContinuar={confirmarConsolidacaoComInconsistencias}
                periodo={periodoFormatoConsulta()}
                itens={inconsistencias}
                titulo="Antes de consolidar, dê uma olhada nisso"
                descricao={`Encontramos ${inconsistencias.length} faturamento(s) com informações pendentes, como código de integração, tipo de serviço ou número de série.`}
                avisoExtra="Caso este período seja consolidado sem corrigir as inconsistências, essas pendências só poderão ser corrigidas manualmente na integração com o SISDM. Se possível, volte e corrija antes de continuar."
                labelContinuar="Continuar mesmo assim"
                labelFechar="Voltar e corrigir"
            />
        </PainelCard>
    );
};

export default FaturamentoCard;
